import * as React from 'react';
import { UsersService } from '@shared/services/UsersService';
import { UserGroupsService } from '@shared/users/user-groups/UserGroupsService';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { cs } from '@shared/utils/utils-classname';
import { Message } from 'primereact/message';
import { Tooltip } from 'primereact/tooltip';
import { Button } from '@shared/ui-react/Button';

interface IState {
    users: IUser[];
    groups: any[];
    checked: Object;
    isLoading: boolean;
    usersError: string;
    groupsError: string;
}

export class EmailUsers extends React.Component<
    {
        onChange?: () => void;
        defaultScrollHeight?: string;
    },
    IState
> {
    state: IState = {
        users: [],
        groups: [],
        checked: {}, // can't be @observable, primereact wont react to it,
        isLoading: true,
        usersError: null,
        groupsError: null,
    };

    filteredRows = [];

    componentDidMount() {
        UsersService.fetchUsersWithEmails()
            .done(users => {
                users = users
                    .map(vd => {
                        vd.selected = false;
                        return vd;
                    })
                    .sort((a, b) => a.fullName.localeCompare(b.fullName));

                UserGroupsService.fetchGroups(null, kuura.getProjectUuid())
                    .then(groups => {
                        const usersIndexByUuid = users.reduce((acc, v) => ({ ...acc, [v.uuid]: v }), {});

                        groups.forEach(group => {
                            group.members.forEach(userInGroup => {
                                if (usersIndexByUuid[userInGroup]) {
                                    usersIndexByUuid[userInGroup].groups.push({
                                        uuid: group.uuid,
                                        name: group.name,
                                    });
                                }
                            });
                        });

                        users.forEach(user => {
                            if (user.groups.length) {
                                user.groupUuids = user.groups.map(g => g.uuid);
                                user.groupsNameString = user.groups.length === 1 ? user.groups[0].name : `${user.groups.length} groups`;
                            }

                            user.groupsIndex = user.groups.reduce((acc, v) => ({ ...acc, [v.id]: v }), {});
                        });

                        this.setState({
                            users,
                            groups,
                            isLoading: false,
                            usersError: null,
                            groupsError: null,
                        });
                    })
                    .catch(e => {
                        console.log(e);

                        this.setState({
                            users,
                            isLoading: false,
                            usersError: null,
                            groupsError: e.errorMsg?.toLowerCase() === 'unauthorized' ? 'No permission to load groups' : 'Failed to load groups',
                        });
                    })
                    .finally(() => {
                        this.filteredRows = users;
                    });
            })
            .fail(e => {
                console.log(e);
                this.setState({
                    isLoading: false,
                    groupsError: null,
                    usersError: e.errorMsg?.toLowerCase() === 'unauthorized' ? 'No permission to load users' : 'Failed to load users',
                });
            });
    }

    render() {
        return (
            <div className={cs('tab-wrapper', { 'overlay-loading': this.state.isLoading })}>
                {this.state.usersError && <Message severity='warn' text={this.state.usersError} style={{ width: '100%' }}></Message>}
                <DataTable
                    value={this.state.users}
                    scrollable
                    scrollHeight={this.props.defaultScrollHeight || '300px'}
                    filterDisplay='row'
                    filterDelay={100}
                    cellMemo={false}
                    onValueChange={r => this.onFilterChanged(r)}
                    emptyMessage={__('general.no_results_found')}
                    onRowClick={row => this.toggleRow(row.data.id)}
                    rowClassName={row => ({ clickable: true, 'p-highlight': this.state.checked[row.id] })}
                >
                    {/* can't use column width here currently - https://github.com/primefaces/primereact/issues/2723 */}
                    <Column
                        style={{ flexBasis: 50, flexGrow: 0 }}
                        className='ellipsis'
                        filter={true}
                        showFilterMenu={false}
                        showClearButton={false}
                        filterElement={
                            <div style={{ width: 40, textAlign: 'center' }}>
                                <input type='checkbox' onChange={e => this.toggleAllVisibleRows(e.currentTarget.checked)} />
                            </div>
                        }
                        body={row => (
                            <div style={{ width: 40, textAlign: 'center' }}>
                                <input type='checkbox' onChange={() => this.toggleRow(row.id)} checked={!!this.state.checked[row.id]} />
                            </div>
                        )}
                    ></Column>

                    <Column
                        header={__('general.role')}
                        field='rolesString'
                        filterField='rolesString'
                        className='ellipsis'
                        filter
                        showFilterMenu={false}
                        showClearButton={false}
                        filterElement={opts => (
                            <select onChange={e => this.onRoleFilterChange(opts, e)}>
                                <option value='all'>{kuura.getMessage('general.all')}</option>
                                {UsersService.uniqueRolesForUsers(this.state.users).map(role => (
                                    <option value={role.friendlyName} key={role.id}>
                                        {role.friendlyName}
                                    </option>
                                ))}
                            </select>
                        )}
                    />
                    <Column
                        header={__('general.group')}
                        className='ellipsis'
                        filter
                        showFilterMenu={false}
                        showClearButton={false}
                        filterField='groupUuids'
                        filterMatchMode='contains'
                        filterElement={opts => (
                            <>
                                {this.state.groupsError ? (
                                    <Button element='div' tooltip={this.state.groupsError} icon='iki iki-warning' iconSize={14} />
                                ) : (
                                    <select onChange={e => this.onGroupFilterChange(opts, e)}>
                                        <option value='all'>{kuura.getMessage('general.all')}</option>
                                        {this.state.groups.map(group => (
                                            <option value={group.uuid} key={group.uuid}>
                                                {group.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </>
                        )}
                        body={row => {
                            if (!row.groups.length) return null;
                            if (row.groups.length === 1) return row.groupsNameString;
                            return (
                                <>
                                    <Tooltip position='top' target={'.js-user-row-' + row.uuid} baseZIndex={99999}>
                                        <div style={{ fontSize: '0.85em', gap: 5 }} className='flex-column'>
                                            {row.groups.map(g => (
                                                <div key={g.name}>{g.name}</div>
                                            ))}
                                        </div>
                                    </Tooltip>
                                    <div className={`ellipsis js-user-row-${row.uuid}`}>{row.groupsNameString}</div>
                                </>
                            );
                        }}
                    />
                    <Column header={__('general.name')} field='fullName' filter showFilterMenu={false} filterMatchMode='contains' className='ellipsis' />
                </DataTable>
                <footer>
                    {__('general.selected_x')}: {this.getCheckedIds().length}
                </footer>
            </div>
        );
    }

    toggleRow = uuid => {
        const checked = this.state.checked;
        if (checked[uuid]) {
            delete checked[uuid];
        } else {
            checked[uuid] = true;
        }
        this.setChecked(checked);
    };

    deselectAll = () => {
        this.setChecked({});
    };

    toggleAllVisibleRows(state: boolean) {
        if (state) {
            this.setChecked(
                this.filteredRows.reduce((obj, curr) => {
                    obj[curr.id] = true;
                    return obj;
                }, {})
            );
        } else {
            this.setChecked({});
        }
    }

    getCheckedIds(): number[] {
        return Object.keys(this.state.checked).map(n => parseInt(n));
    }

    onFilterChanged = rows => {
        this.filteredRows = rows;
    };

    private setChecked = checked => {
        this.setState(
            {
                checked,
            },
            () => {
                if (typeof this.props.onChange === 'function') {
                    this.props.onChange();
                }
            }
        );
    };

    private onRoleFilterChange = (opts, e) => {
        const val = e.currentTarget.value;
        opts.filterApplyCallback(val === 'all' ? null : val);
    };

    private onGroupFilterChange = (opts, e) => {
        const val = e.currentTarget.value;
        opts.filterApplyCallback(val === 'all' ? null : val);
    };
}
